import * as React from 'react';
import { makeStyles, mergeClasses, Text, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusXLarge,
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '12px',
        paddingTop: '16px',
        paddingBottom: '12px',
        paddingLeft: '20px',
        paddingRight: '20px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        '@media (max-width: 767px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
    },
    headerText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minWidth: 0,
    },
    title: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase400,
    },
    description: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    action: { flexShrink: 0 },
    body: {
        paddingTop: '16px',
        paddingBottom: '20px',
        paddingLeft: '20px',
        paddingRight: '20px',
        '@media (max-width: 767px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
    },
    bodyFlush: {
        padding: 0,
    },
});

export interface ISectionProps {
    title?: React.ReactNode;
    description?: React.ReactNode;
    action?: React.ReactNode;
    /** Removes the body padding (for tables and lists with their own spacing). */
    flush?: boolean;
    className?: string;
    children: React.ReactNode;
}

/** White panel with an optional header. The workhorse container of every page. */
const Section: React.FC<ISectionProps> = ({ title, description, action, flush, className, children }) => {
    const styles = useStyles();
    return (
        <section className={mergeClasses(styles.root, className)}>
            {(title || action) && (
                <header className={styles.header}>
                    <div className={styles.headerText}>
                        {title && <Text as="h2" className={styles.title}>{title}</Text>}
                        {description && <Text className={styles.description}>{description}</Text>}
                    </div>
                    {action && <div className={styles.action}>{action}</div>}
                </header>
            )}
            <div className={mergeClasses(styles.body, flush && styles.bodyFlush)}>{children}</div>
        </section>
    );
};

export default Section;
